import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router';
import axios from 'axios';
import {
    Box, Paper, Typography, Button, CircularProgress,
    Alert, Chip, Divider,
} from '@mui/material';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import ArticleIcon from '@mui/icons-material/Article';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import CloudIcon from '@mui/icons-material/Cloud';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { TokenContext } from '../Context/TokenContext';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

function formatBytes(bytes) {
    if (!bytes) return '0 o';
    const k = 1024;
    const sizes = ['o', 'Ko', 'Mo', 'Go'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function FileIcon({ mimetype }) {
    const sx = { fontSize: 56 };
    if (!mimetype) return <ArticleIcon sx={{ ...sx, color: '#757575' }} />;
    if (mimetype === 'application/pdf') return <PictureAsPdfIcon sx={{ ...sx, color: '#a20000' }} />;
    if (mimetype.startsWith('image/')) return <ImageIcon sx={{ ...sx, color: '#1e88e5' }} />;
    if (mimetype.includes('word') || mimetype.includes('document') || mimetype.includes('text/'))
        return <DescriptionIcon sx={{ ...sx, color: '#43a047' }} />;
    if (mimetype.includes('zip') || mimetype.includes('rar') || mimetype.includes('compressed'))
        return <FolderZipIcon sx={{ ...sx, color: '#fb8c00' }} />;
    return <ArticleIcon sx={{ ...sx, color: '#757575' }} />;
}

export default function SecureSharePage() {
    const { share_id } = useParams();
    const navigate = useNavigate();
    const { accessToken } = useContext(TokenContext);

    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [downloaded, setDownloaded] = useState(false);

    useEffect(() => {
        if (!accessToken) return;
        const fetch = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API_URL}/api/drive/secure-share/${share_id}`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                setInfo(res.data);
            } catch (e) {
                setError(e.response?.data?.error || 'Accès refusé ou partage invalide');
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [accessToken, share_id]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const res = await axios.get(`${API_URL}/api/drive/secure-share/${share_id}/download`, {
                headers: { Authorization: `Bearer ${accessToken}` },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = info?.nom_original || 'fichier';
            a.click();
            window.URL.revokeObjectURL(url);
            setDownloaded(true);
        } catch (e) {
            setError(e.response?.data?.error || 'Erreur lors du téléchargement');
        } finally {
            setDownloading(false);
        }
    };

    if (!accessToken) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <CloudIcon color="primary" sx={{ fontSize: 32 }} />
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>Drive — Partage sécurisé</Typography>
                </Box>
                <Paper variant="outlined" sx={{ maxWidth: 440, width: '100%', p: { xs: 2, sm: 4 }, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <LockPersonIcon color="primary" sx={{ fontSize: 56 }} />
                        <Typography variant="h6" fontWeight="bold">Connexion requise</Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            Ce fichier est partagé de façon sécurisée. Vous devez être connecté pour y accéder.
                        </Typography>
                        <Button variant="contained" color="primary"
                            onClick={() => navigate('/authentication/callback')}>
                            Se connecter
                        </Button>
                    </Box>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <CloudIcon color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>Drive — Partage sécurisé</Typography>
            </Box>

            <Paper variant="outlined" sx={{ maxWidth: 480, width: '100%', p: { xs: 2, sm: 4 }, borderRadius: 2 }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress color="primary" />
                    </Box>
                )}

                {!loading && error && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>
                        <Button onClick={() => navigate('/')}>Retour à l'accueil</Button>
                    </Box>
                )}

                {!loading && info && (
                    <Box>
                        {/* Security badge */}
                        <Alert
                            icon={<CheckCircleIcon fontSize="small" />}
                            severity="success"
                            sx={{ mb: 3, borderRadius: 2 }}
                        >
                            Accès sécurisé — Ce partage est lié à votre compte
                        </Alert>

                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                            <FileIcon mimetype={info.mimetype} />
                            <Typography variant="h6" fontWeight="bold" sx={{ mt: 1, textAlign: 'center', wordBreak: 'break-word' }}>
                                {info.nom_original}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                                <Chip label={formatBytes(info.taille)} size="small" />
                                {info.mimetype && <Chip label={info.mimetype.split('/')[1]?.toUpperCase() || info.mimetype} size="small" />}
                                {(info.permissions || []).map(p => (
                                    <Chip key={p} label={p} size="small" variant="outlined" color="primary" />
                                ))}
                            </Box>
                        </Box>

                        {info.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                                {info.description}
                            </Typography>
                        )}

                        <Divider sx={{ mb: 2 }} />

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 3 }}>
                            {info.createur && (
                                <Typography variant="caption" color="text.secondary">
                                    Partagé par : {info.createur}
                                </Typography>
                            )}
                            {info.date_upload && (
                                <Typography variant="caption" color="text.secondary">
                                    Déposé le : {new Date(info.date_upload).toLocaleDateString('fr-FR')}
                                </Typography>
                            )}
                        </Box>

                        {downloaded && (
                            <Alert severity="success" sx={{ mb: 2 }}>Téléchargement démarré !</Alert>
                        )}

                        {(info.permissions || []).includes('telecharger') ? (
                            <Button
                                fullWidth variant="contained" size="large"
                                startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                                onClick={handleDownload}
                                disabled={downloading}
                                sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}
                            >
                                {downloading ? 'Téléchargement...' : 'Télécharger'}
                            </Button>
                        ) : (
                            <Alert severity="info">Vous avez uniquement accès à la consultation de ce fichier.</Alert>
                        )}
                    </Box>
                )}
            </Paper>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                Ce fichier est partagé de manière sécurisée via Drive
            </Typography>
        </Box>
    );
}
